import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import {
  type ComponentProps,
  forwardRef,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn';

export function Checkbox({ className, ...props }: ComponentProps<typeof CheckboxPrimitive.Root>) {
  return (
    <CheckboxPrimitive.Root
      className={cn(
        'grid size-3.5 shrink-0 place-items-center rounded-sm border border-border-strong bg-surface-1 transition-colors duration-(--dur-fast) focus-visible:outline-none focus-visible:shadow-ring data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        <Check className="size-2.5 text-accent-fg" strokeWidth={3} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'min-h-[96px] w-full rounded border border-border-strong bg-surface-1 px-pad-x py-2 text-base text-text-1 outline-none transition-[border-color,box-shadow] duration-(--dur-fast) placeholder:text-text-3 focus:border-accent focus:shadow-glow',
        invalid && 'border-danger focus:border-danger focus:shadow-none',
        className,
      )}
      {...props}
    />
  );
});

/** Abas com sublinhado no accent (skill → components.md §Tabs). */
export function Tabs<T extends string>({
  value,
  onChange,
  tabs,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  tabs: { value: T; label: ReactNode; count?: number }[];
  className?: string;
}) {
  return (
    <div role="tablist" className={cn('flex gap-1 border-b border-border', className)}>
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            className={cn(
              '-mb-px flex h-9 items-center gap-1.5 border-b-2 border-transparent px-2.5 text-base text-text-2 transition-colors duration-(--dur-fast) hover:text-text-1 focus-visible:outline-none focus-visible:shadow-ring',
              active && 'border-accent text-text-1',
            )}
          >
            {t.label}
            {t.count !== undefined ? (
              <span className="font-mono text-xs text-text-3">{t.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** Segmento de página: título + ações (skill → layout.md §Cabeçalho de página). */
export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-gap">
      <div className="min-w-0">
        {eyebrow}
        <h1 className="text-xl font-semibold leading-tight">{title}</h1>
        {subtitle ? <p className="mt-0.5 text-sm text-text-2">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ml-auto flex shrink-0 items-center gap-gap">{actions}</div> : null}
    </div>
  );
}
